/**
 * Horizontal bar chart showing task count per status, coloured by category.
 */
import type { StatusDistributionItem } from "../../lib/api";
import { normaliseToWidth, categoryColor } from "./chartHelpers";

interface Props {
  items: StatusDistributionItem[];
}

const MAX_BAR_W = 220;
const ROW_H = 20;
const LABEL_W = 80;
const COUNT_W = 30;
const PAD_X = 8;
const PAD_Y = 4;

export function StatusDistributionChart({ items }: Props): preact.JSX.Element {
  // Only show statuses that exist (have a count slot)
  const visible = items.filter(() => true); // show all — even 0-count statuses so users see the full workflow
  if (visible.length === 0) {
    return (
      <p class="text-label italic text-[color:var(--color-on-surface-variant)]">
        Keine Statuses konfiguriert
      </p>
    );
  }

  const counts = visible.map((it) => it.count);
  const barWidths = normaliseToWidth(counts, MAX_BAR_W);

  const svgH = visible.length * ROW_H + PAD_Y * 2;
  const svgW = LABEL_W + MAX_BAR_W + COUNT_W + PAD_X * 2;

  return (
    <svg
      viewBox={`0 0 ${svgW} ${svgH}`}
      style={{ width: "100%", height: "auto" }}
      aria-label="Status-Verteilung"
    >
      {visible.map((item, i) => {
        const y = PAD_Y + i * ROW_H;
        const cy = y + ROW_H / 2;
        const barX = PAD_X + LABEL_W;
        const bw = barWidths[i] ?? 0;
        const color = categoryColor(item.category);
        return (
          <g key={item.status_id}>
            {/* Status label */}
            <text
              x={PAD_X}
              y={cy}
              dominant-baseline="middle"
              font-size="9"
              fill="currentColor"
              opacity={0.75}
            >
              {item.status_name.length > 12
                ? `${item.status_name.slice(0, 11)}…`
                : item.status_name}
            </text>

            {/* Background track */}
            <rect
              x={barX}
              y={cy - 5}
              width={MAX_BAR_W}
              height={10}
              rx={3}
              fill="currentColor"
              opacity={0.07}
            />
            {/* Value bar */}
            {bw > 0 && (
              <rect
                x={barX}
                y={cy - 5}
                width={bw}
                height={10}
                rx={3}
                fill={color}
                opacity={0.85}
              >
                <title>{`${item.status_name}: ${item.count}`}</title>
              </rect>
            )}

            {/* Count label */}
            <text
              x={barX + MAX_BAR_W + PAD_X}
              y={cy}
              dominant-baseline="middle"
              font-size="9"
              fill="currentColor"
              opacity={0.7}
            >
              {item.count}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
